/**
 * Calendar dates — `YYYY-MM-DD`, no time, no zone.
 *
 * PURE, like everything in this package: `nzDate` takes the instant rather than reading a
 * clock. Rent periods, tenancy terms, ownership intervals and accounting periods are all
 * DATES, not instants, and the distinction is load-bearing in New Zealand: NZ observes
 * daylight saving, so a local-time "day" is 23 or 25 hours twice a year. Arithmetic that
 * treats a day as 24 hours of local time lands on the wrong date every October and April.
 *
 * Everything here works in UTC, where a day is always 86,400,000 ms, and only `nzDate` knows
 * about a timezone at all.
 */

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Parse to a UTC instant, refusing anything that is not a real day.
 *
 * The round-trip is the check, and it is not pedantry: `Date.parse('2026-02-30')` succeeds and
 * silently means 2 March. A tenancy typed as ending on 30 February would be stored two days
 * later than anyone intended and nothing would ever say so.
 */
function utcOf(date: string): number {
  const match = DATE_RE.exec(date);
  if (!match) throw new RangeError(`Not a date: ${JSON.stringify(date)}`);

  const ms = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (new Date(ms).toISOString().slice(0, 10) !== date) {
    throw new RangeError(`Not a real date: ${JSON.stringify(date)}`);
  }
  return ms;
}

export function isCalendarDate(value: string): boolean {
  try {
    utcOf(value);
    return true;
  } catch {
    return false;
  }
}

/** Move a calendar date by whole days. See the note above on why this is UTC. */
export function addDays(date: string, days: number): string {
  return new Date(utcOf(date) + days * 86_400_000).toISOString().slice(0, 10);
}

/** The day before. Used to close an ownership or a tenancy the day its successor begins. */
export const previousDay = (date: string): string => addDays(date, -1);

/**
 * Move a calendar date forward by whole WEEKDAYS — Monday to Friday, Saturday and Sunday skipped.
 *
 * NZ PUBLIC HOLIDAYS ARE NOT ACCOUNTED FOR. There is no holiday calendar in this codebase, and
 * building one correctly means Waitangi Day, ANZAC Day and Mondayisation at the national level
 * plus a regional anniversary day that differs by agency — a real subsystem, not a date helper.
 * Every caller of this function must say so where its result is shown; see
 * `bondLodgementDueEstimate` for the wording. `count` must be positive — this only ever moves
 * forward, which is the only direction a deadline estimate needs.
 */
export function addWeekdays(date: string, count: number): string {
  if (count < 0) throw new RangeError('addWeekdays only moves forward');

  let result = date;
  let remaining = count;
  while (remaining > 0) {
    result = addDays(result, 1);
    const day = new Date(`${result}T00:00:00Z`).getUTCDay();
    if (day !== 0 && day !== 6) remaining -= 1;
  }
  return result;
}

/** Whole days from `from` to `to`, inclusive of both ends. A one-day span is 1. */
export function daysBetween(from: string, to: string): number {
  return Math.round((utcOf(to) - utcOf(from)) / 86_400_000) + 1;
}

/** How many days April has. Leap years included, which is the only reason this is a function. */
export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/**
 * Move a calendar date by whole months, CLAMPING to the length of the month it lands in.
 *
 * `addMonths('2026-01-31', 1)` is `2026-02-28`, not 3 March. Date arithmetic that overflows
 * into the next month is how a rent charge dated the 31st lands in the wrong accounting period
 * every February.
 *
 * ALWAYS CALL THIS FROM THE ANCHOR, never from the previous result. Stepping one month at a
 * time from a 31st anchor gives 31 Jan → 28 Feb → 28 Mar → 28 Apr: the clamp becomes permanent
 * and the tenant is billed three days early for the rest of the tenancy. Computing
 * `addMonths(anchor, k)` for each k gives 31 Jan → 28 Feb → 31 Mar, which is what the tenancy
 * agreement says. `rent.ts` depends on this and a property test pins it.
 */
export function addMonths(date: string, months: number): string {
  const match = DATE_RE.exec(date);
  if (!match) throw new RangeError(`Not a date: ${JSON.stringify(date)}`);

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);

  // Months since year zero, so December + 1 needs no special case.
  const absolute = year * 12 + (month - 1) + months;
  const targetYear = Math.floor(absolute / 12);
  const targetMonth = (absolute % 12) + 1;

  const clamped = Math.min(day, daysInMonth(targetYear, targetMonth));
  return `${String(targetYear).padStart(4, '0')}-${String(targetMonth).padStart(2, '0')}-${String(clamped).padStart(2, '0')}`;
}

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * The day of the week a date falls on — "Wednesday".
 *
 * UTC, like everything else here, and for the same reason: the weekday of a calendar date is a
 * property of the date, not of where the reader is standing. `toLocaleDateString` would answer
 * "Tuesday" for a NZ rent day on a browser in Los Angeles.
 */
export function weekdayName(date: string): string {
  const day = new Date(utcOf(date)).getUTCDay();
  // Unreachable — getUTCDay returns 0–6 and the array has seven entries.
  return WEEKDAYS[day] ?? date;
}

/**
 * ISO weekday of a calendar date: 1 is Monday, 7 is Sunday.
 *
 * ISO rather than JavaScript's 0-is-Sunday, because the number is STORED — an owner paid "weekly
 * on a Friday" carries a 5 — and a stored 0 that means Sunday is the kind of off-by-one nobody
 * catches until an agency's Sunday owners are paid on Saturday.
 */
export function isoWeekday(date: string): number {
  const day = new Date(utcOf(date)).getUTCDay();
  return day === 0 ? 7 : day;
}

/** `5` → `Friday`. The inverse of `isoWeekday`, for a sentence a person reads. */
export function isoWeekdayName(weekday: number): string {
  // Unreachable for a validated 1–7; the fallback keeps this total rather than returning
  // `undefined` into a sentence.
  return WEEKDAYS[weekday % 7] ?? String(weekday);
}

/** `4` → `4th`, for "due on the 21st of the month". */
export function ordinal(n: number): string {
  const tens = n % 100;
  if (tens >= 11 && tens <= 13) return `${n}th`;
  const suffix = { 1: 'st', 2: 'nd', 3: 'rd' }[n % 10] ?? 'th';
  return `${n}${suffix}`;
}

/**
 * A calendar date as a New Zealander writes it: `01/06/2025`.
 *
 * `YYYY-MM-DD` is how dates are stored, compared and sent over the wire — it sorts
 * lexicographically, it is unambiguous, and nothing in this codebase should stop using it.
 * This is the other end: what a property manager reads on a screen. NZ writes day first, and a
 * tenancy running "2025-06-01 – present" is a database row showing through the paint.
 *
 * STRING SURGERY, not `toLocaleDateString`. Two reasons, both of which have bitten this
 * codebase before. `new Date('2025-06-01')` parses as UTC midnight and renders as 31 May
 * anywhere west of Greenwich, which is every browser in the Americas and some CI runners. And
 * a locale-driven format depends on the runtime's ICU data, so the same build would render
 * `6/1/2025` on a machine that happens to negotiate `en-US` — day and month silently swapped,
 * which for a tenancy start date is not a cosmetic bug.
 *
 * Anything that is not a `YYYY-MM-DD` date is returned unchanged rather than becoming
 * `NaN/NaN/NaN`: a date that fails to parse should still show the operator what is stored.
 */
export function formatNzDate(date: string): string {
  const match = DATE_RE.exec(date);
  if (!match) return date;
  return `${match[3]}/${match[2]}/${match[1]}`;
}

/** `formatNzDate`, but null-tolerant — an open-ended span reads as a word, not a blank. */
export function formatNzDateOr(date: string | null | undefined, fallback: string): string {
  return date == null || date === '' ? fallback : formatNzDate(date);
}

/**
 * What day it is in New Zealand at a given instant.
 *
 * Takes the instant so this stays pure and testable. `en-CA` is the locale that formats as
 * `YYYY-MM-DD`, which is the format every date column and every comparison in this codebase
 * uses — building the string by hand from the parts is the same thing with more places to get
 * the padding wrong.
 *
 * The server runs in UTC and an agency's evening is the next UTC day for half the year. A
 * tenancy started "today" at 8pm on 30 June in Auckland must not be recorded as 1 July.
 */
export function nzDate(instant: Date): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Pacific/Auckland' }).format(instant);
}

/**
 * An instant, split into the New Zealand day and time of day it happened at.
 *
 * FOR THE THINGS THAT ARE INSTANTS RATHER THAN DAYS. Almost every date in this product is a
 * plain calendar date — a rent period, a tenancy start — and those never want a clock. The audit
 * log is the exception: "signed in at 09:14" and "signed in at 21:14 the same evening" are
 * different facts about the same person, and the second one is the one somebody asks about.
 *
 * The date half comes back ISO so it can be handed to `formatNzDate` like any other date, rather
 * than this inventing a second day-first formatter that could disagree with it.
 *
 * 24-HOUR, via `hourCycle` rather than `hour12: false` — the latter renders midnight as "24:00"
 * in some ICU versions, which is an hour that does not exist on the day it is printed beside.
 */
export function nzDateTimeParts(instant: Date): { date: string; time: string } {
  return {
    date: nzDate(instant),
    time: new Intl.DateTimeFormat('en-NZ', {
      timeZone: 'Pacific/Auckland',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(instant),
  };
}
